import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { useEventListener } from '@elfsight/lib-utils';
import { useSettings } from '@wu/widget-core';
import { classnamePrefix } from '../../../../../constants';
import { Image } from '../../../common';
import { currentVideoContext } from '../../../currentVideoContext';
import {
  DEFAULT_VIDEO_HEIGHT,
  DEFAULT_VIDEO_WIDTH,
  DOCUMENT_EVENTS,
  EVENTS_LIST,
  getParsedMessageData,
  getSpacing,
  getVideoURL,
  METHODS_LIST,
  postMessage
} from './helpers';

export const VimeoPlayer = ({
  url,
  autoplay,
  loop,
  onPlay,
  onPause,
  onEnd,
  item
}) => {
  const { pictures, width, height } = item ?? {};
  const { sizes = [] } = pictures ?? {};

  const { renderThumbnailMode, playMode } = useSettings();
  const eventCalledRef = useRef(false);
  const iframeRef = useRef(null);
  const methodsRef = useRef({ onPlay, onPause, onEnd });
  const [size, setSize] = useState({
    width: width || DEFAULT_VIDEO_WIDTH,
    height: height || DEFAULT_VIDEO_HEIGHT
  });
  const { videoState, setVideoState } = useContext(currentVideoContext);

  const videoURL = getVideoURL({ url, autoplay, loop });

  useEffect(() => {
    methodsRef.current = {
      onPlay,
      onPause,
      onEnd
    };
  }, [onPlay, onPause, onEnd]);

  const handlePostMessage = useCallback(
    (action, value = null) =>
      postMessage({
        element: iframeRef.current,
        action,
        value
      }),
    []
  );

  const eventHandler = useCallback(() => {
    if (!eventCalledRef.current) {
      handlePostMessage('play');
      eventCalledRef.current = true;
    }
  }, []);

  useEffect(() => {
    if (!autoplay) {
      return;
    }

    DOCUMENT_EVENTS.forEach((event) => {
      document.addEventListener(event, eventHandler, { once: true });
    });

    return () => {
      DOCUMENT_EVENTS.forEach((event) => {
        document.removeEventListener(event, eventHandler);
      });
    };
  }, [autoplay]);

  const onReady = useCallback(() => {
    EVENTS_LIST.forEach((event) => {
      handlePostMessage('addEventListener', event);
    });
    METHODS_LIST.forEach((method) => {
      handlePostMessage(method);
    });
  }, []);

  const handleEventsAndMethods = useCallback((event) => {
    const events = {
      ready: () => onReady(),
      pause: () => methodsRef.current.onPause(),
      finish: () => methodsRef.current.onEnd(),
      play: () => methodsRef.current.onPlay()
    };

    const methods = {
      getVideoWidth: () =>
        setSize((prevState) => ({
          ...prevState,
          width: event?.value || DEFAULT_VIDEO_WIDTH
        })),
      getVideoHeight: () =>
        setSize((prevState) => ({
          ...prevState,
          height: event?.value || DEFAULT_VIDEO_HEIGHT
        }))
    };

    if (event.event) {
      return events[event.event]?.();
    }

    return methods[event.method]?.();
  }, []);

  const handleMessage = useCallback((event) => {
    const isTargetFrame = event.source === iframeRef.current?.contentWindow;
    if (isTargetFrame) {
      handleEventsAndMethods(getParsedMessageData(event.data));
    }
  }, []);

  useEffect(() => {
    if (videoState) {
      handlePostMessage(videoState);
      setVideoState(null);
    }
  }, [videoState]);

  useEventListener('message', handleMessage, window);

  return (
    <Component _size={size} _playMode={playMode}>
      {renderThumbnailMode ? (
        <Preview>
          <Image imagesArray={sizes} />
        </Preview>
      ) : (
        <Player
          ref={iframeRef}
          src={videoURL}
          title="Vimeo Player"
          width="100%"
          height="100%"
          allow="autoplay; fullscreen"
          mozallowfullscreen="true"
          webkitallowfullscreen="true"
          allowFullScreen
        />
      )}
    </Component>
  );
};

const Component = styled.div.attrs({
  className: `${classnamePrefix}-vimeo-player-component`
})`
  position: ${({ _playMode }) =>
    _playMode === 'inline' ? 'static' : 'relative'};
  width: 100%;
  height: 0;
  overflow: hidden;
  transition: padding-top 0.3s;
  padding-top: ${({ _size }) => `${getSpacing(_size)}%`};
`;

const Preview = styled.div.attrs({
  className: `${classnamePrefix}-vimeo-player-preview`
})`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: #000;
`;

const Player = styled.iframe.attrs({
  className: `${classnamePrefix}-vimeo-player-player`
})`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: #000;
  border: none;
`;
